import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTelegram, FaEnvelope, FaLock, FaHeart, FaShieldAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import HolyLogo from '../components/HolyLogo';

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.darkBg2};
  color: ${props => props.theme.colors.text};
  padding: 4rem 2rem 2rem;
  margin-top: 4rem;
  position: relative;
  overflow: hidden;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(90deg, 
      ${props => props.theme.colors.primary}, 
      ${props => props.theme.colors.accent},
      ${props => props.theme.colors.secondary},
      ${props => props.theme.colors.primary}
    );
    background-size: 400% 400%;
    animation: gradient 15s ease infinite;
  }
  
  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;

const FooterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  font-weight: 700;
  font-family: ${props => props.theme.fonts.headings};
  margin-bottom: 1.5rem;
  background: ${props => props.theme.colors.gradient1};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  svg {
    margin-right: 0.75rem;
    font-size: 2.2rem;
    animation: ${pulseAnimation} 2s ease-in-out infinite;
    color: ${props => props.theme.colors.primary};
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  width: 100%;
  margin-bottom: 3rem;
  text-align: left;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterSectionTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  font-family: ${props => props.theme.fonts.headings};
  
  &::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
    bottom: -8px;
    left: 0;
    border-radius: 2px;
    opacity: 0.7;
  }
`;

const FooterText = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.6;
  opacity: 0.8;
  max-width: 600px;
  color: ${props => props.theme.colors.textMuted};
  
  &.copyright {
    margin-top: 2rem;
    font-size: 0.9rem;
    opacity: 0.6;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FooterLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.text};
  opacity: 0.9;
  transition: ${props => props.theme.transition.default};
  
  &:hover {
    opacity: 1;
    transform: translateX(5px);
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    margin-right: 0.8rem;
    font-size: 1rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const ExternalLink = styled.a`
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.text};
  opacity: 0.9;
  transition: ${props => props.theme.transition.default};
  
  &:hover {
    opacity: 1;
    transform: translateX(5px);
    color: ${props => props.theme.colors.primary};
  }
  
  svg {
    margin-right: 0.8rem;
    font-size: 1.2rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const FooterDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
  margin: 2rem 0;
`;

const HeartIcon = styled(FaHeart)`
  color: ${props => props.theme.colors.accent};
  animation: ${pulseAnimation} 1.5s ease-in-out infinite;
  margin: 0 0.3rem;
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterLogo>
          <HolyLogo style={{ fontSize: '2.5rem', marginRight: '-0.50rem' }} />
          olyHubs
        </FooterLogo>
        
        <FooterGrid>
          <FooterSection>
            <FooterSectionTitle>À Propos</FooterSectionTitle>
            <FooterText>
              HolyHubs est une plateforme dédiée à la sécurisation et à la modération des échanges 
              au sein de notre communauté Telegram active.
            </FooterText>
          </FooterSection>
          
          <FooterSection>
            <FooterSectionTitle>Liens Rapides</FooterSectionTitle>
            <FooterLinks>
              <FooterLink to="/scam-list">
                <FaLock /> Scamlist
              </FooterLink>
              <FooterLink to="/report">
                <FaEnvelope /> Signaler un Scammer
              </FooterLink>
              <FooterLink to="/admin/login">
                <HolyLogo style={{ marginRight: '0.5rem', fontSize: '1rem' }} /> Espace Admin
              </FooterLink>
            </FooterLinks>
          </FooterSection>
          
          <FooterSection>
            <FooterSectionTitle>Nous Contacter</FooterSectionTitle>
            <FooterLinks>
              <ExternalLink href="https://t.me/holyhubs" target="_blank" rel="noopener noreferrer">
                <FaTelegram /> Rejoindre notre Telegram
              </ExternalLink>
            </FooterLinks>
          </FooterSection>
        </FooterGrid>
        
        <FooterDivider />
        
        <FooterText className="copyright">
          &copy; {new Date().getFullYear()} HolyHubs. Tous droits réservés. Conçu avec <HeartIcon /> pour la communauté.
        </FooterText>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer; 